import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, query, where, getDocs, updateDoc, addDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { MessageSquare, Heart, MessageCircle, Send, MoreVertical, Edit2, Trash2, X, AlertTriangle } from 'lucide-static';
import { auth } from '../../firebase';
import './DocumentComments.css';

// Icon wrapper component
const Icon = ({ icon, className, size = 20, color, fill, ...props }) => {
  const getProcessedSvg = () => {
    let svgString = icon;
    if (size) svgString = svgString.replace("<svg", `<svg width="${size}" height="${size}"`);
    if (color) svgString = svgString.replace("<svg", `<svg color="${color}" stroke="${color}"`);
    if (fill) {
      if (svgString.includes('fill="none"')) {
        svgString = svgString.replace('fill="none"', `fill="${fill}"`);
      } else {
        svgString = svgString.replace("<svg", `<svg fill="${fill}"`);
      }
    }
    if (className) svgString = svgString.replace("<svg", `<svg class="${className}"`);
    return svgString;
  };
  return <span dangerouslySetInnerHTML={{ __html: getProcessedSvg() }} {...props} />;
};

const DocumentComments = ({ documentId, isAdmin = false }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyingTo, setReplyingTo] = useState(null);
  const [newCommentText, setNewCommentText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [editingComment, setEditingComment] = useState(null);
  const [editCommentText, setEditCommentText] = useState('');
  const [commentToDelete, setCommentToDelete] = useState(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setActiveDropdown(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!documentId) return;

    const fullDocId = documentId.startsWith('documents:') ? documentId : `documents:${documentId}`;

    const commentsRef = collection(db, 'comments'); // The user hasn't specified the exact name, assuming 'comments'
    const q = query(
      commentsRef,
      where('documentId', '==', fullDocId)
    );

    let isMounted = true;

    const fetchComments = async () => {
      try {
        const rawId = documentId.replace('documents:', '');
        const idFormats = [`documents:${rawId}`, rawId];
        
        console.log("Fetching comments for documentIds:", idFormats);
        const possibleCollections = [
          'comments', 'community_forum', 'communityForums', 
          'forum', 'forums', 'chats', 'posts', 'document_comments', 'community'
        ];
        
        let fetchedComments = [];
        let successfulCollection = null;

        for (const colName of possibleCollections) {
          for (const testId of idFormats) {
            try {
              const colRef = collection(db, colName);
              const q = query(colRef, where('documentId', '==', testId));
              const snap = await getDocs(q);
              if (!snap.empty) {
                console.log(`FOUND ${snap.size} comments in collection: '${colName}' using ID format: '${testId}'`);
                successfulCollection = colName;
                snap.forEach(d => fetchedComments.push({ id: d.id, ...d.data() }));
                break; 
              }
            } catch (e) {
               console.error(`Error querying ${colName} with ${testId}:`, e);
            }
          }
          if (successfulCollection) break;
        }

        if (fetchedComments.length === 0) {
          console.warn("Could not find any comments. Check if the collection name is missing from the list, or if the documentId field doesn't exactly match.");
        }

        // Sort comments locally
        fetchedComments.sort((a, b) => {
          const timeA = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
          const timeB = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
          return timeA - timeB;
        });

        if (isMounted) {
          setComments(fetchedComments);
          setLoading(false);
        }
      } catch (error) {
        console.error("Critical error in fetchComments: ", error);
        if (isMounted) setLoading(false);
      }
    };

    fetchComments();

    return () => {
      isMounted = false;
    };
  }, [documentId, refreshTrigger]);

  const handleLike = async (comment) => {
    if (!auth.currentUser) {
      alert("You must be logged in to like comments.");
      return;
    }
    
    const userId = auth.currentUser.uid;
    const hasLiked = comment.upvotedBy?.includes(userId);
    
    // Optimistic UI update
    setComments(prev => prev.map(c => {
      if (c.id === comment.id) {
        return {
          ...c,
          upvotes: hasLiked ? Math.max(0, (c.upvotes || 0) - 1) : (c.upvotes || 0) + 1,
          upvotedBy: hasLiked 
            ? (c.upvotedBy || []).filter(id => id !== userId)
            : [...(c.upvotedBy || []), userId]
        };
      }
      return c;
    }));

    try {
      const commentRef = doc(db, 'comments', comment.id);
      const newUpvotedBy = hasLiked 
        ? (comment.upvotedBy || []).filter(id => id !== userId)
        : [...(comment.upvotedBy || []), userId];
        
      await updateDoc(commentRef, {
        upvotes: newUpvotedBy.length,
        upvotedBy: newUpvotedBy
      });
    } catch (error) {
      console.error("Error updating like:", error);
      // If it fails, refresh to get real state
      setRefreshTrigger(prev => prev + 1);
    }
  };

  const handleSubmitComment = async (parentId = null) => {
    if (!newCommentText.trim() || !auth.currentUser) return;

    setIsSubmitting(true);
    try {
      const fullDocId = documentId.startsWith('documents:') ? documentId : `documents:${documentId}`;
      const newComment = {
        documentId: fullDocId,
        sourceCollection: "documents",
        text: newCommentText.trim(),
        userId: auth.currentUser.uid,
        userName: auth.currentUser.displayName || "Admin",
        userPhoto: auth.currentUser.photoURL || null,
        parentId: parentId,
        upvotes: 0,
        upvotedBy: [],
        isEdited: false,
        createdAt: serverTimestamp()
      };

      await addDoc(collection(db, 'comments'), newComment);
      
      setNewCommentText('');
      setReplyingTo(null);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error("Error adding comment:", error);
      alert("Failed to post comment.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteComment = (commentId) => {
    setCommentToDelete(commentId);
  };

  const confirmDelete = async () => {
    if (!commentToDelete) return;
    setIsSubmitting(true);
    
    const childrenToDelete = getChildren(commentToDelete);
    const childIds = childrenToDelete.map(c => c.id);
    
    // Optimistic UI Update: instantly remove from the screen
    setComments(prev => prev.filter(c => c.id !== commentToDelete && !childIds.includes(c.id)));
    setCommentToDelete(null); // Close modal instantly

    try {
      await deleteDoc(doc(db, 'comments', commentToDelete));
      
      for (const child of childrenToDelete) {
        await deleteDoc(doc(db, 'comments', child.id));
      }
      
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error("Error deleting comment:", error);
      alert("Failed to delete comment.");
      setRefreshTrigger(prev => prev + 1); // Revert optimistic update if failed
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSubmit = async (commentId) => {
    if (!editCommentText.trim()) return;
    setIsSubmitting(true);
    try {
      const commentRef = doc(db, 'comments', commentId);
      await updateDoc(commentRef, {
        text: editCommentText.trim(),
        isEdited: true
      });
      setEditingComment(null);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error("Error editing comment:", error);
      alert("Failed to edit comment.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const startEditing = (comment) => {
    setEditingComment(comment.id);
    setEditCommentText(comment.text);
    setReplyingTo(null);
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'Unknown time';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  // Group comments into threads (parents and children)
  const parents = comments.filter(c => !c.parentId);
  const getChildren = (parentId) => comments.filter(c => c.parentId === parentId);

  if (loading) {
    return (
      <div className="document-comments-container">
        <div className="loading-spinner">Loading comments...</div>
      </div>
    );
  }

  return (
    <div className="document-comments-container">
      <div className="document-comments-header">
        <h3 className="document-comments-title">Community Forum</h3>
        <span style={{ color: '#6B7280', fontSize: '14px' }}>
          {comments.length} {comments.length === 1 ? 'comment' : 'comments'}
        </span>
      </div>

      {comments.length === 0 ? (
        <div className="document-comments-empty">
          <Icon icon={MessageSquare} size={48} />
          <p>No comments on this document yet.</p>
        </div>
      ) : (
        <div className="comment-list">
          {parents.map((parent) => (
            <div key={parent.id} className="comment-thread">
              {/* Parent Comment */}
              <div className="comment-item">
                <div className="comment-avatar">
                  {parent.userPhoto ? (
                    <img src={parent.userPhoto} alt={parent.userName} />
                  ) : (
                    getInitials(parent.userName)
                  )}
                </div>
                <div className="comment-content">
                  <div className="comment-header">
                    <span className="comment-author">{parent.userName || 'Unknown User'}</span>
                    <span className="comment-time">{formatTime(parent.createdAt)}</span>
                    {parent.isEdited && <span className="comment-edited">(edited)</span>}
                    
                    {(auth.currentUser?.uid === parent.userId || isAdmin) && (
                      <div className="comment-options-wrapper" onClick={e => e.stopPropagation()}>
                        <button 
                          className="comment-options-btn"
                          onClick={() => setActiveDropdown(activeDropdown === parent.id ? null : parent.id)}
                        >
                          <Icon icon={MoreVertical} size={16} />
                        </button>
                        
                        {activeDropdown === parent.id && (
                          <div className="comment-dropdown">
                            {auth.currentUser?.uid === parent.userId && (
                              <button className="comment-dropdown-item" onClick={() => { setActiveDropdown(null); startEditing(parent); }}>
                                <Icon icon={Edit2} size={14} /> Edit
                              </button>
                            )}
                            <button className="comment-dropdown-item delete" onClick={() => { setActiveDropdown(null); handleDeleteComment(parent.id); }}>
                              <Icon icon={Trash2} size={14} /> Delete
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {editingComment === parent.id ? (
                    <div className="comment-input-container edit-mode">
                      <input 
                        type="text"
                        className="comment-input-box"
                        value={editCommentText}
                        onChange={(e) => setEditCommentText(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleEditSubmit(parent.id); }}
                      />
                      <button 
                        className="comment-send-btn" 
                        disabled={!editCommentText.trim() || isSubmitting}
                        onClick={() => handleEditSubmit(parent.id)}
                      >
                        <Icon icon={Send} size={18} />
                      </button>
                      <button className="reply-cancel-btn" onClick={() => setEditingComment(null)} style={{position: 'absolute', right: '-40px', top: '16px'}}>
                        <Icon icon={X} size={20} />
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="comment-text">{parent.text}</div>
                      <div className="comment-actions">
                        <button 
                          className={`comment-action-btn ${parent.upvotedBy?.includes(auth.currentUser?.uid) ? 'liked' : ''}`}
                          onClick={() => handleLike(parent)}
                        >
                          <Icon icon={Heart} size={14} fill="currentColor" />
                          {parent.upvotes || 0}
                        </button>
                        <button className="comment-action-btn" onClick={() => { setReplyingTo(replyingTo === parent.id ? null : parent.id); setEditingComment(null); }}>
                          <Icon icon={MessageCircle} size={14} />
                          Reply
                        </button>
                      </div>
                    </>
                  )}
                  
                  {replyingTo === parent.id && (
                    <div className="comment-input-container reply-mode">
                      <input 
                        type="text"
                        className="comment-input-box"
                        placeholder={`Reply to ${parent.userName}...`}
                        value={newCommentText}
                        onChange={(e) => setNewCommentText(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSubmitComment(parent.id); }}
                      />
                      <button 
                        className="comment-send-btn" 
                        disabled={!newCommentText.trim() || isSubmitting}
                        onClick={() => handleSubmitComment(parent.id)}
                      >
                        <Icon icon={Send} size={18} />
                      </button>
                    </div>
                  )}
                </div>
              </div>

               {/* Child Replies Thread */}
              {getChildren(parent.id).length > 0 && (
                <div className="comment-thread-replies">
                  {getChildren(parent.id).map((child) => (
                    <div key={child.id} className="comment-item reply">
                      <div className="comment-avatar" style={{ width: '32px', height: '32px', fontSize: '12px' }}>
                        {child.userPhoto ? (
                          <img src={child.userPhoto} alt={child.userName} />
                        ) : (
                          getInitials(child.userName)
                        )}
                      </div>
                      <div className="comment-content">
                        <div className="comment-header">
                          <span className="comment-author">{child.userName || 'Unknown User'}</span>
                          <span className="comment-time">{formatTime(child.createdAt)}</span>
                          {child.isEdited && <span className="comment-edited">(edited)</span>}
                          
                          {(auth.currentUser?.uid === child.userId || isAdmin) && (
                            <div className="comment-options-wrapper" onClick={e => e.stopPropagation()}>
                              <button 
                                className="comment-options-btn"
                                onClick={() => setActiveDropdown(activeDropdown === child.id ? null : child.id)}
                              >
                                <Icon icon={MoreVertical} size={16} />
                              </button>
                              
                              {activeDropdown === child.id && (
                                <div className="comment-dropdown">
                                  {auth.currentUser?.uid === child.userId && (
                                    <button className="comment-dropdown-item" onClick={() => { setActiveDropdown(null); startEditing(child); }}>
                                      <Icon icon={Edit2} size={14} /> Edit
                                    </button>
                                  )}
                                  <button className="comment-dropdown-item delete" onClick={() => { setActiveDropdown(null); handleDeleteComment(child.id); }}>
                                    <Icon icon={Trash2} size={14} /> Delete
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {editingComment === child.id ? (
                          <div className="comment-input-container edit-mode">
                            <input 
                              type="text"
                              className="comment-input-box"
                              value={editCommentText}
                              onChange={(e) => setEditCommentText(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') handleEditSubmit(child.id); }}
                            />
                            <button 
                              className="comment-send-btn" 
                              disabled={!editCommentText.trim() || isSubmitting}
                              onClick={() => handleEditSubmit(child.id)}
                            >
                              <Icon icon={Send} size={18} />
                            </button>
                            <button className="reply-cancel-btn" onClick={() => setEditingComment(null)} style={{position: 'absolute', right: '-40px', top: '16px'}}>
                              <Icon icon={X} size={20} />
                            </button>
                          </div>
                        ) : (
                          <>
                            <div className="comment-text">{child.text}</div>
                            <div className="comment-actions">
                              <button 
                                className={`comment-action-btn ${child.upvotedBy?.includes(auth.currentUser?.uid) ? 'liked' : ''}`}
                                onClick={() => handleLike(child)}
                              >
                                <Icon icon={Heart} size={14} fill="currentColor" />
                                {child.upvotes || 0}
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Main Reply Box */}
      {replyingTo === null && (
        <div className="comment-input-container">
          <input 
            type="text"
            className="comment-input-box"
            placeholder="Write a comment..."
            value={newCommentText}
            onChange={(e) => setNewCommentText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSubmitComment(null); }}
          />
          <button 
            className="comment-send-btn" 
            disabled={!newCommentText.trim() || isSubmitting}
            onClick={() => handleSubmitComment(null)}
          >
            <Icon icon={Send} size={18} />
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {commentToDelete && (
        <div className="filter-modal-overlay" style={{ zIndex: 9999 }}>
          <div className="delete-modal-content">
            <div className="delete-modal-header">
              <div className="delete-warning-icon">
                <Icon icon={AlertTriangle} size={32} />
              </div>
              <h3 className="delete-modal-title">Delete Comment</h3>
              <p className="delete-modal-subtitle">
                Are you sure you want to delete this comment? This action cannot be undone.
              </p>
            </div>
            <div className="delete-modal-footer">
              <button className="delete-cancel-btn" onClick={() => setCommentToDelete(null)}>Cancel</button>
              <button className="delete-confirm-btn" onClick={confirmDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentComments;
